// ─────────────────────────────────────────────
//  useTeamDirectory — the department's contacts, live, from ATC's own data.
//
//  Anesthesia Team Control (ATC)
//  Copyright © 2026 Gonzalo Martinez (gamc00@gmail.com). All rights reserved.
//  Proprietary and confidential. Not licensed for public use — see /LICENSE.
// ─────────────────────────────────────────────
//
//  ⚠️ THIS WAS ONE DOCUMENT AND IS NOW THREE COLLECTIONS. Until 2026-08-19 the
//  page read `team_directory/current`, a single doc mirrored weekly out of a
//  world-readable Google Sheet — one listener, one read. The owner ended that
//  dependency ("I want nothing public"), so the source is now `staff`,
//  `surgeons` and `facilities`: ATC's own records, edited by admins in the app.
//
//  ⚠️ THREE LISTENERS. NOT ONE PER PERSON. The cost went from 1 read to
//  "however many contacts this tenant has", which is the price of owning the
//  data — but it must stay a FIXED number of collection subscriptions. The
//  moment anything here reads a document per row (a facility per person, a
//  user per staff record) opening the directory becomes hundreds of reads and
//  a phone on hospital wifi feels it. Everything a row needs is already in the
//  row's own document; joining is done in memory, once, in domain/teamContacts.
//
//  ⚠️ IT SUBSCRIBES ONLY WHILE SIGNED IN, AND RE-SUBSCRIBES ON EVERY SIGN-IN.
//  An unconditional subscription at mount can attach before Firebase Auth has
//  restored the session; Firestore denies it, the listener dies, and the page
//  shows a refusal notice to somebody who is perfectly entitled to read it.
//  useStaff.js learned this the hard way (empty staff pools after a cold
//  login); the same `onAuth` shape is used here.
//
//  ⚠️ FOUR DIFFERENT NOTHINGS, AND THEY MUST NOT BE CONFLATED:
//    · loading           — not every source has delivered its first snapshot.
//    · empty             — they all have, and nobody has been added yet. NOT a
//                          fault, and no longer "the sync has not run": there is
//                          no sync. An admin adds people in Setup.
//    · error (fatal)     — the PEOPLE read was refused or failed. Observers,
//                          schedulers and the unattended wall devices are denied
//                          by the rules on purpose, and so is any account that
//                          is not at one of this tenant's campuses. A denial is
//                          an expected outcome and must arrive as a sentence.
//    · a secondary miss  — `surgeons` or `facilities` failed while `staff`
//                          worked. That section renders empty and the page
//                          stays up. Blanking a working directory because one
//                          of three collections is unreachable (a rule not yet
//                          deployed, an offline cache miss) trades a small gap
//                          for a total outage.
//
//  Returns arrays that are stable across renders when nothing changed, because
//  a fresh array per render feeds effect dependencies downstream and has
//  crashed the vitest worker in this repo before.
// ─────────────────────────────────────────────
import { useEffect, useState } from "react";
import { onAuth } from "../firebase/auth";
// Three collection listeners and nothing else. `listenToSurgeons` is the newest
// of them (the `surgeons` collection is new in Team v2) and is imported BY NAME
// like the other two on purpose: if it ever goes away, CI=true turns webpack's
// "export not found" into a failed build, which is the loud failure this repo
// wants. The `typeof` guard in attach() below is for the shim and the tests,
// not for a missing export.
import { listenToFacilities, listenToStaff, listenToSurgeons } from "../firebase/firestore";

// Module-level constants, deliberately shared: every "empty" render hands back
// the SAME array rather than a new one.
const NO_DOCS = [];
const NOTHING_SETTLED = { staff: false, surgeons: false, facilities: false };

const toRecords = (snap) =>
  snap && Array.isArray(snap.docs)
    ? snap.docs.map((d) => ({ id: d.id, ...(typeof d.data === "function" ? d.data() : {}) }))
    : NO_DOCS;

export function useTeamDirectory() {
  const [staff, setStaff] = useState(NO_DOCS);
  const [surgeons, setSurgeons] = useState(NO_DOCS);
  const [facilities, setFacilities] = useState(NO_DOCS);
  const [settled, setSettled] = useState(NOTHING_SETTLED);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    let subs = [];
    const stopAll = () => { subs.forEach((u) => { if (typeof u === "function") u(); }); subs = []; };

    // One subscription. `fatal` marks the source whose refusal is the page's
    // refusal — see the header's fourth "nothing".
    const attach = (key, listen, setRecords, fatal) => {
      const settle = () => setSettled((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
      if (typeof listen !== "function") { settle(); return null; }
      return listen(
        (snap) => {
          if (!live) return;
          setRecords(toRecords(snap));
          settle();
        },
        (err) => {
          if (!live) return;
          // Kept verbatim rather than folded into a generic "something went
          // wrong": a permission-denied here is a deliberate refusal by the
          // rules, and the page's wording depends on being able to tell.
          console.error(`Team directory listener failed (${key}):`, err);
          if (fatal) setError(err?.code || "unavailable");
          settle();
        }
      );
    };

    const unsubAuth = onAuth((authUser) => {
      if (!live) return;
      stopAll();
      if (!authUser) {
        setStaff(NO_DOCS);
        setSurgeons(NO_DOCS);
        setFacilities(NO_DOCS);
        setSettled(NOTHING_SETTLED);
        setError(null);
        return;
      }
      setSettled(NOTHING_SETTLED);
      setError(null);
      subs = [
        attach("staff", listenToStaff, setStaff, true),
        attach("surgeons", listenToSurgeons, setSurgeons, false),
        attach("facilities", listenToFacilities, setFacilities, false),
      ].filter(Boolean);
    });

    return () => { live = false; unsubAuth(); stopAll(); };
  }, []);

  return {
    staff,
    surgeons,
    facilities,
    loading: !(settled.staff && settled.surgeons && settled.facilities),
    error,
  };
}
